import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for the global "session expired" event dispatched by the api
  // interceptor when /auth/refresh fails. Flip the provider state to "anon"
  // immediately so route guards re-evaluate without waiting for the page
  // redirect — keeps protected components from continuing to fire stale
  // requests while the redirect is in flight.
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("auth-session-expired", onExpired);
    return () => window.removeEventListener("auth-session-expired", onExpired);
  }, []);

  // Cross-tab session synchronisation.
  //
  // Browsers share httpOnly cookies AND localStorage across every tab of the
  // same origin/profile, so two tabs CANNOT hold two distinct sessions in
  // the same browser. When the operator logs in with account B in tab #2,
  // tab #1's UI keeps showing account A's stale React state until the next
  // navigation — and any new API call silently uses B's token, producing
  // the "mixed accounts" effect the user reported.
  //
  // The fix is the same one used by Notion / Linear / Stripe Dashboard:
  // listen for the `storage` event (fired by the BROWSER on every other tab
  // when localStorage mutates) and on token change auto-refresh the user
  // state. If the new token decodes to a different user, we hard-reload
  // the tab so every cached component (clients list, settings, dashboards)
  // is rehydrated for the right account, with no leftover state.
  //
  // We also re-validate when the tab is brought back to focus (`visibilitychange`)
  // — handles the case where the user logged in/out from another tab while
  // this tab was in background and didn't fire `storage` (some Safari
  // versions throttle storage events for background tabs).
  useEffect(() => {
    const TOKEN_KEY = "abm_access_token";

    const handleStorage = (e) => {
      // Ignore unrelated keys + same-tab writes (storage event fires only
      // cross-tab by spec, but be defensive).
      if (e.key && e.key !== TOKEN_KEY) return;
      // localStorage was cleared (logout from another tab) → drop our state.
      if (e.key === TOKEN_KEY && e.newValue === null) {
        setUser(false);
        return;
      }
      // Token changed → revalidate. If it now points to a different user,
      // hard-reload to rehydrate all cached dashboards/clients/settings.
      const prevUserId = user && user !== false ? user.id : null;
      api.get("/auth/me")
        .then(({ data }) => {
          if (prevUserId && data?.id && data.id !== prevUserId) {
            // Different account just logged in elsewhere — full reload to
            // wipe any stale React Query cache, sidebar state, etc.
            window.location.reload();
          } else {
            setUser(data);
          }
        })
        .catch(() => setUser(false));
    };

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // On tab refocus, just ask the backend who we are. This is cheap
      // (single /auth/me roundtrip) and covers the Safari background
      // throttling gap without forcing a reload when nothing changed.
      const prevUserId = user && user !== false ? user.id : null;
      api.get("/auth/me")
        .then(({ data }) => {
          if (prevUserId && data?.id && data.id !== prevUserId) {
            window.location.reload();
          } else if (prevUserId !== (data?.id ?? null)) {
            setUser(data || false);
          }
        })
        .catch(() => {});
    };

    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [user]);

  const storeAuth = (data) => {
    if (data?.access_token) setToken(data.access_token);
    // Strip token from user object before storing in state
    const { access_token, ...userData } = data || {};
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return storeAuth(data);
  };

  const register = async (name, email, password, accepted_privacy = false, accepted_terms = false) => {
    const { data } = await api.post("/auth/register", {
      name, email, password,
      accepted_privacy: !!accepted_privacy,
      accepted_terms: !!accepted_terms,
    });
    return storeAuth(data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    setUser(false);
  };

  const updateModules = async (modules, primary_module = undefined) => {
    const payload = { modules };
    if (primary_module) payload.primary_module = primary_module;
    const { data } = await api.put("/users/modules", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, updateModules }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
